/* eslint-disable @typescript-eslint/naming-convention */

import type { MetricKey } from "./config/tabsConfig";

// API Response Types
/**
 * Geography Graph API Response - time-series data organized by metric type and country
 */
export interface GeographyGraphResponse {
    /** Visit data by country and date */
    Visits: Record<string, Record<string, number>>;
    /** Pages per visit data by country and date */
    PagesPerVisit: Record<string, Record<string, number>>;
    /** Time per visit data by country and date */
    TimePerVisit: Record<string, Record<string, number>>;
    /** Bounce rate data by country and date */
    BounceRate: Record<string, Record<string, number>>;
    /** Growth data by country and date */
    Growth: Record<string, Record<string, number>>;
}

/**
 * Individual country data item from table response
 */
interface CountryTableItem {
    /** Country ID */
    Country: number;
    /** Traffic share percentage (0-1) */
    TrafficShare: number;
    /** Total visits count */
    Visits: number;
    /** Growth in absolute numbers */
    Growth: number;
    /** Growth as percentage (0-1) */
    GrowthPercentage: number;
    /** Time spent per visit in seconds */
    TimePerVisit: number;
    /** Average pages per visit */
    PagesPerVisit: number;
    /** Bounce rate percentage (0-1) */
    BounceRate: number;
}

/**
 * Geography Table API Response - tabular data with country statistics
 */
export interface GeographyTableResponse {
    /** Total number of countries */
    TotalCount: number;
    /** Array of country data records */
    Data: CountryTableItem[];
}

// Legacy/Extended types for UI components
export type CountryDataItem = CountryTableItem & {
    CountryName?: string;
    index?: number;
    childsToShow?: number;
    selectionColor?: string;
};

export type GeographyTableData = {
    Data: CountryDataItem[];
    TotalCount?: number;
};

export type IndustryAnalysisApiParams = {
    webSource: string;
    includeSubdomains: boolean;
    category: string;
    from: string;
    to: string;
    isWindow: boolean;
    keys: string;
    timeGranularity: string;
    categoryDisplayApi: string;
};

/**
 * Data point for LineGraph component
 */
export interface LineGraphDataPoint {
    x: number; // timestamp
    y: number | null; // value
}

/**
 * Series data for LineGraph component
 */
export interface LineGraphSeries {
    name: string;
    color: string;
    id: string;
    visible: boolean;
    data: [number, number | null][];
}

/**
 * Legend item for chart display
 */
export interface LegendItem {
    name: string;
    color: string;
    countryId: string | number;
}

/**
 * Selected country data with color assignment
 */
export interface SelectedCountry {
    Country: number;
    CountryName?: string;
    selectionColor: string;
    [key: string]: unknown; // Allow for additional properties from table data
}

/**
 * Raw chart data structure matching API response
 */
export interface ChartData {
    [countryId: string]: {
        [dateString: string]: number | null;
    };
}

/**
 * Complete chart dataset organized by metric
 */
type MetricChartData = {
    [K in MetricKey]?: ChartData;
};

/**
 * Props for the chart component
 */
export interface TrafficAndEngagementChartProps {
    /** Chart data for the selected metric */
    data: LineGraphSeries[];
    /** Legend items to display */
    legends: LegendItem[];
    /** Loading state */
    isLoading?: boolean;
    selectedMetric: MetricKey;
}

/**
 * Props for the tabs component
 */
export interface TrafficAndEngagementTabsProps {
    /** Title for the widget */
    title: string;
    /** Tooltip text for the title */
    titleTooltip?: string;
    /** Chart data organized by metric */
    data: MetricChartData | null;
    /** Currently selected countries from table */
    selectedCountries: SelectedCountry[];
    /** Loading state */
    isLoading?: boolean;
    /** Excel export URL */
    excelUrl?: string;
    /** Filter function for country name display */
    countryNameFilter: (countryId: string | number) => string;
    /** Date range for subtitle */
    dateRange: {
        from: string;
        to: string;
    };
    /** Web source for subtitle */
    webSource: string;
}

/**
 * Simplified props for the new tabs component
 */
export interface SimplifiedTrafficAndEngagementTabsProps {
    /** Chart data organized by metric */
    graphData: MetricChartData | null;
    /** Currently selected countries from table */
    selectedCountries: SelectedCountry[];
    /** Loading state */
    isLoading?: boolean;
    /** Excel export URL */
    excelUrl?: string;
    /** Filter function for country name display */
    countryNameFilter: (countryId: string | number) => string;
}

// Comparison Table Types
/**
 * Country data structure for comparison table rows
 */
interface GeographyComparisonTableCountryData {
    /** Traffic share percentage */
    trafficShare: number;
    /** Total visits count */
    visits: number;
    /** Growth in absolute numbers */
    growth: number;
    /** Growth as percentage change */
    change: number;
}

/**
 * Row structure for the geography comparison table
 */
export interface GeographyComparisonTableRow {
    /** Country ID */
    country: number;
    /** Data for the primary category being compared */
    primaryCategory: GeographyComparisonTableCountryData | null;
    /** Data for the compared category */
    comparedCategory: GeographyComparisonTableCountryData | null;
}

// Map Data Types
/**
 * Base map data item structure for Highcharts
 */
interface BaseMapDataItem {
    /** Highcharts country key */
    "hc-key": string;
}

export interface SingleMapDataItem extends BaseMapDataItem {
    value: number;
}

/**
 * Extended map data item for comparison mode
 */
export interface ComparisonMapDataItem extends BaseMapDataItem {
    /** Primary category value */
    primaryValue: number;
    /** Compared category value */
    comparedValue: number;
    /** Color for comparison visualization */
    color: string;
}

/**
 * Union type for map data items
 */
export type MapDataItem = SingleMapDataItem | ComparisonMapDataItem;

// Type guard to check if a map data item is a comparison item
export const isComparisonMapDataItem = (item: MapDataItem): item is ComparisonMapDataItem => {
    return "primaryValue" in item && "comparedValue" in item;
};
